import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Modal, { PopupButton, PopupButtonTone, PopupVariant } from 'components/Modal';

interface BaseOptions {
  title?: string;
  message?: React.ReactNode;
  variant?: PopupVariant;
}

export interface ConfirmOptions extends BaseOptions {
  confirmLabel?: string;
  cancelLabel?: string;
  /** Override the confirm button colour (defaults to danger for the 'danger' variant, else primary) */
  confirmTone?: PopupButtonTone;
}

export interface AlertOptions extends BaseOptions {
  okLabel?: string;
}

export interface OpenOptions extends BaseOptions {
  /** Arbitrary set of buttons. The popup promise resolves with the clicked button's `value`. */
  buttons: PopupButton[];
  /** When false, hides the X and disables backdrop/Escape dismissal (default true) */
  dismissable?: boolean;
  /** Value resolved when the popup is dismissed via backdrop/Escape/X */
  dismissValue?: any;
}

export interface PopupApi {
  /** Fully custom popup — any buttons. Resolves with the clicked button's value. */
  open: (opts: OpenOptions) => Promise<any>;
  /** Yes/No style popup. Resolves true if confirmed, false if cancelled/dismissed. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Single-button acknowledgement popup. Resolves when dismissed. */
  alert: (opts: AlertOptions) => Promise<void>;
}

interface InternalState {
  open: boolean;
  title?: string;
  message?: React.ReactNode;
  variant: PopupVariant;
  buttons: PopupButton[];
  dismissable: boolean;
  dismissValue: any;
}

const CLOSED: InternalState = {
  open: false,
  variant: 'question',
  buttons: [],
  dismissable: true,
  dismissValue: undefined,
};

const PopupContext = createContext<PopupApi | null>(null);

/**
 * Provides a single app-wide popup and an imperative API to drive it.
 * Wrap the app once, then call `usePopup()` from any component to replace
 * native window.confirm()/alert() with a styled, promise-based popup.
 */
export const PopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<InternalState>(CLOSED);
  const resolverRef = useRef<((value: any) => void) | null>(null);

  const settle = useCallback((value: any) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
    if (resolve) resolve(value);
  }, []);

  const open = useCallback((opts: OpenOptions): Promise<any> => {
    return new Promise<any>((resolve) => {
      // If a popup is somehow already open, resolve it as dismissed first
      // so its awaiter never hangs.
      if (resolverRef.current) {
        const prev = resolverRef.current;
        resolverRef.current = null;
        prev(undefined);
      }
      resolverRef.current = resolve;
      setState({
        open: true,
        title: opts.title,
        message: opts.message,
        variant: opts.variant || 'question',
        buttons: opts.buttons,
        dismissable: opts.dismissable !== false,
        dismissValue: opts.dismissValue,
      });
    });
  }, []);

  const confirm = useCallback(
    (opts: ConfirmOptions): Promise<boolean> => {
      const variant = opts.variant || 'question';
      const confirmTone: PopupButtonTone = opts.confirmTone || (variant === 'danger' ? 'danger' : 'primary');
      return open({
        title: opts.title,
        message: opts.message,
        variant,
        dismissValue: false,
        buttons: [
          { label: opts.cancelLabel || 'Cancel', value: false, tone: 'secondary' },
          { label: opts.confirmLabel || 'Confirm', value: true, tone: confirmTone, autoFocus: true },
        ],
      });
    },
    [open]
  );

  const alert = useCallback(
    (opts: AlertOptions): Promise<void> => {
      return open({
        title: opts.title,
        message: opts.message,
        variant: opts.variant || 'info',
        dismissValue: undefined,
        buttons: [{ label: opts.okLabel || 'OK', value: undefined, tone: 'primary', autoFocus: true }],
      }).then(() => undefined);
    },
    [open]
  );

  const api = useMemo<PopupApi>(() => ({ open, confirm, alert }), [open, confirm, alert]);

  return (
    <PopupContext.Provider value={api}>
      {children}
      <Modal
        open={state.open}
        title={state.title}
        message={state.message}
        variant={state.variant}
        buttons={state.buttons}
        dismissable={state.dismissable}
        onButtonClick={settle}
        onDismiss={() => settle(state.dismissValue)}
      />
    </PopupContext.Provider>
  );
};

export const usePopup = (): PopupApi => {
  const ctx = useContext(PopupContext);
  if (!ctx) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return ctx;
};
